import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { ASSETS } from "../../data/site";
import { useAdminAuth, formatApiErrorDetail } from "../../context/AdminAuth";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#E8EBE9] bg-white px-4 py-3 text-[#111E16] outline-none transition-shadow focus:ring-2 focus:ring-[#2F5E43]/30";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FBFBF9] px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <img src={ASSETS.logoIcon} alt="SisterSafe" className="h-10 w-10 object-contain" />
          <span className="font-heading text-2xl font-semibold tracking-tight text-[#111E16]">
            SisterSafe
          </span>
        </div>

        <form
          onSubmit={submit}
          className="rounded-3xl border border-[#2F5E43]/10 bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
          data-testid="admin-login-form"
        >
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#8AA583]">
            <Lock className="h-3.5 w-3.5" /> Admin Access
          </div>
          <h1 className="font-heading text-2xl font-semibold text-[#111E16]">Sign in</h1>
          <p className="mt-1.5 text-sm text-[#4A5F52]">
            This area is private. Authorized administrators only.
          </p>

          {error && (
            <div
              className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <div className="mt-5">
            <label className="mb-1.5 block text-sm font-medium text-[#111E16]">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@sistersafe.app"
              className={inputCls}
              data-testid="admin-email-input"
            />
          </div>
          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-medium text-[#111E16]">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputCls}
              data-testid="admin-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#2F5E43] px-7 py-3.5 font-medium text-white transition-colors hover:bg-[#244A34] disabled:opacity-70"
            data-testid="admin-login-submit"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4" /> Sign in
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
